import { useMemo, useState } from "react";
import { useGetQuotes, getGetQuotesQueryKey, useGetTags, getGetTagsQueryKey } from "@workspace/api-client-react";
import {
  ERA_VALUES, QUOTE_FORM_VALUES, SOURCE_TYPE_VALUES, STATUS_VALUES, TEXT_LIMIT, VISIBILITY_VALUES, WORK_TYPE_VALUES,
  codePoints, humanize, type CurationForm,
} from "./curationUtils";

const input =
  "w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-400 focus:ring-offset-1 disabled:bg-stone-50 disabled:text-stone-500";
const label = "mb-1 block text-xs font-semibold uppercase tracking-wide text-stone-500";

function Select({ id, value, onChange, options, allowEmpty, labelText }: {
  id: string; value: string; onChange: (v: string) => void; options: readonly (string | null)[]; allowEmpty?: string; labelText: string;
}) {
  return (
    <div>
      <label htmlFor={id} className={label}>{labelText}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={input}>
        {allowEmpty !== undefined && <option value="">{allowEmpty}</option>}
        {options.filter((o): o is string => !!o).map((o) => <option key={o} value={o}>{humanize(o)}</option>)}
      </select>
    </div>
  );
}

function AuthorLookup({ value, onChange, locked, lockedNote }: { value: string; onChange: (v: string) => void; locked: boolean; lockedNote: string }) {
  const [focused, setFocused] = useState(false);
  const term = value.trim();
  const params = { query: term, pageSize: 20, page: 1 };
  const { data, isFetching } = useGetQuotes(params, {
    query: { queryKey: getGetQuotesQueryKey(params), enabled: !locked && focused && term.length >= 2, staleTime: 30_000 },
  });
  const names = useMemo(() => {
    const set = new Set<string>();
    (data?.data ?? []).forEach((q) => { if (q.author.toLowerCase().includes(term.toLowerCase())) set.add(q.author); });
    return Array.from(set).slice(0, 6);
  }, [data, term]);

  if (locked) {
    return (
      <div>
        <span className={label}>Author</span>
        <div className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-600">{value}</div>
        <p className="mt-1 text-xs text-stone-400">{lockedNote}</p>
      </div>
    );
  }
  return (
    <div className="relative">
      <label htmlFor="cur-author" className={label}>Canonical author / speaker</label>
      <input
        id="cur-author" className={input} value={value} autoComplete="off"
        role="combobox" aria-expanded={focused && names.length > 0} aria-controls="cur-author-list"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => window.setTimeout(() => setFocused(false), 150)}
      />
      {focused && term.length >= 2 && (names.length > 0 || isFetching) && (
        <ul id="cur-author-list" role="listbox" className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-stone-200 bg-white shadow-lg">
          {isFetching && names.length === 0 && <li className="px-3 py-2 text-xs text-stone-400">Searching existing authors</li>}
          {names.map((n) => (
            <li key={n} role="option" aria-selected={n === value}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(n); setFocused(false); }}
                className="w-full px-3 py-2 text-left text-sm text-stone-700 hover:bg-stone-50 focus:bg-stone-50 focus:outline-none">
                {n}
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-xs text-stone-400">Pick an existing author where possible. The notability gate still applies on save.</p>
    </div>
  );
}

export function CuratorFields({ form, setForm, original }: {
  form: CurationForm; setForm: (f: CurationForm) => void; original: { isOriginal: boolean; ownerName: string | null };
}) {
  const { data: tags = [] } = useGetTags({ query: { queryKey: getGetTagsQueryKey(), staleTime: 300_000 } });
  const [tagFilter, setTagFilter] = useState("");
  const set = <K extends keyof CurationForm>(k: K, v: CurationForm[K]) => setForm({ ...form, [k]: v });
  const len = codePoints(form.text);
  const selected = tags.filter((t) => form.tagIds.includes(t.id));
  const pool = tags.filter((t) => !form.tagIds.includes(t.id) && t.name.toLowerCase().includes(tagFilter.toLowerCase())).slice(0, 24);

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="cur-text" className={label}>Quote text</label>
        <textarea id="cur-text" rows={4} className={`${input} resize-y font-serif text-base leading-relaxed`} value={form.text} onChange={(e) => set("text", e.target.value)} />
        <p className={`mt-1 text-xs ${len > TEXT_LIMIT ? "text-red-600" : "text-stone-400"}`}>{len}/{TEXT_LIMIT} characters</p>
      </div>

      <AuthorLookup
        value={form.author}
        onChange={(v) => set("author", v)}
        locked={original.isOriginal}
        lockedNote={`Community originals stay credited to their owner${original.ownerName ? ` (${original.ownerName})` : ""}. Ownership cannot be reassigned here.`}
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Select id="cur-era" labelText="Era" value={form.era} onChange={(v) => set("era", v)} options={ERA_VALUES} allowEmpty="Unset" />
        <div>
          <label htmlFor="cur-year" className={label}>Year</label>
          <input id="cur-year" inputMode="numeric" placeholder="e.g. -44" className={input} value={form.year} onChange={(e) => set("year", e.target.value)} />
        </div>
        <div>
          <label htmlFor="cur-lang" className={label}>Language</label>
          <input id="cur-lang" placeholder="e.g. en" className={input} value={form.language} onChange={(e) => set("language", e.target.value)} />
        </div>
      </div>

      <div>
        <label htmlFor="cur-work" className={label}>Work / source</label>
        <input id="cur-work" className={input} value={form.work} onChange={(e) => set("work", e.target.value)} />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Select id="cur-st" labelText="Source type" value={form.sourceType} onChange={(v) => set("sourceType", v)}
          options={original.isOriginal ? ["user_authored"] : SOURCE_TYPE_VALUES.filter((s) => s !== "user_authored")} allowEmpty="Unset" />
        <Select id="cur-qf" labelText="Quote form" value={form.quoteForm} onChange={(v) => set("quoteForm", v)} options={QUOTE_FORM_VALUES} allowEmpty="None" />
        <Select id="cur-wt" labelText="Work type" value={form.workType} onChange={(v) => set("workType", v)} options={WORK_TYPE_VALUES} allowEmpty="None" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Select id="cur-status" labelText="Status" value={form.status} onChange={(v) => set("status", v)} options={STATUS_VALUES} />
        <Select id="cur-vis" labelText="Visibility" value={form.visibility} onChange={(v) => set("visibility", v)} options={VISIBILITY_VALUES} allowEmpty="Unset" />
      </div>

      <fieldset className="flex flex-wrap gap-4 rounded-2xl bg-stone-50 px-4 py-3">
        <legend className="sr-only">Content flags</legend>
        {(["containsProfanity", "containsSexualContext"] as const).map((k) => (
          <label key={k} className="flex cursor-pointer items-center gap-2 text-sm text-stone-700">
            <input type="checkbox" className="h-4 w-4 accent-stone-900" checked={form[k]} onChange={(e) => set(k, e.target.checked)} />
            {k === "containsProfanity" ? "Contains profanity" : "Sexual context"}
          </label>
        ))}
      </fieldset>

      <div>
        <span className={label}>Canonical tags ({form.tagIds.length}/3)</span>
        <div className="mb-2 flex min-h-[2rem] flex-wrap gap-1.5">
          {selected.length === 0 && <span className="text-xs text-stone-400">No tags selected</span>}
          {selected.map((t) => (
            <button key={t.id} type="button" onClick={() => set("tagIds", form.tagIds.filter((id) => id !== t.id))}
              aria-label={`Remove tag ${t.name}`}
              className="rounded-full bg-stone-900 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400">
              {t.name} <span aria-hidden>x</span>
            </button>
          ))}
        </div>
        <input aria-label="Filter tags" placeholder="Filter existing tags" className={input} value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} />
        <div className="mt-2 flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
          {pool.map((t) => (
            <button key={t.id} type="button" disabled={form.tagIds.length >= 3} onClick={() => set("tagIds", [...form.tagIds, t.id])}
              className="rounded-full border border-stone-200 bg-white px-2.5 py-1 text-xs text-stone-600 transition hover:border-stone-300 hover:bg-stone-50 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400">
              {t.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
